<template>
  <div class="password-reset">
    <div class="reset-container">
      <div class="reset-card">
        <!-- Estado de carga inicial -->
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          <h2>Verificando token...</h2>
          <p>Validando la autenticidad de tu solicitud de cambio de contraseña.</p>
        </div>

        <!-- Formulario de nueva contraseña -->
        <div v-else-if="validToken && !resetComplete" class="reset-form-state">
          <div class="header">
            <h2>🔐 Restablecer Contraseña</h2>
            <p>Ingresa tu nueva contraseña para completar el proceso.</p>
          </div>

          <form @submit.prevent="resetPassword" class="reset-form">
            <div class="form-group">
              <label for="newPassword">Nueva Contraseña</label>
              <input
                type="password"
                id="newPassword"
                v-model="newPassword"
                placeholder="Ingresa tu nueva contraseña"
                :class="{ 'error': passwordError }"
                required
              />
              <div class="password-requirements">
                <small>La contraseña debe tener al menos 8 caracteres, incluir una mayúscula, una minúscula, un número y un carácter especial.</small>
              </div>
              <div v-if="passwordError" class="error-message">{{ passwordError }}</div>
            </div>

            <div class="form-group">
              <label for="confirmPassword">Confirmar Contraseña</label>
              <input
                type="password"
                id="confirmPassword"
                v-model="confirmPassword"
                placeholder="Confirma tu nueva contraseña"
                :class="{ 'error': confirmError }"
                required
              />
              <div v-if="confirmError" class="error-message">{{ confirmError }}</div>
            </div>

            <div class="form-actions">
              <button type="submit" class="btn-primary" :disabled="isResetting">
                {{ isResetting ? 'Cambiando contraseña...' : 'Cambiar Contraseña' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Estado de éxito -->
        <div v-else-if="resetComplete" class="success-state">
          <div class="success-icon">✅</div>
          <h2>¡Contraseña cambiada exitosamente!</h2>
          <p>Tu contraseña ha sido actualizada correctamente.</p>
          <p>Ya puedes iniciar sesión con tu nueva contraseña.</p>
          <div class="action-buttons">
            <button @click="goToLogin" class="btn-primary">
              Iniciar Sesión
            </button>
            <button @click="goToHome" class="btn-secondary">
              Ir al Inicio
            </button>
          </div>
        </div>

        <!-- Estado de error -->
        <div v-else-if="!validToken" class="error-state">
          <div class="error-icon">❌</div>
          <h2>Token inválido o expirado</h2>
          <p class="error-message">{{ errorMessage }}</p>
          <div class="error-details">
            <p>Posibles causas:</p>
            <ul>
              <li>El enlace de recuperación ha expirado (válido por 1 hora)</li>
              <li>El enlace ya fue utilizado anteriormente</li>
              <li>El enlace es inválido o está corrupto</li>
            </ul>
            <p>Por favor, solicita un nuevo enlace de recuperación.</p>
          </div>
          <div class="action-buttons">
            <button @click="goToForgotPassword" class="btn-primary">
              Solicitar Nuevo Enlace
            </button>
            <button @click="goToHome" class="btn-secondary">
              Ir al Inicio
            </button>
          </div>
        </div>

        <!-- Estado de token faltante -->
        <div v-else class="error-state">
          <div class="error-icon">⚠️</div>
          <h2>Token de recuperación no encontrado</h2>
          <p>No se encontró un token de recuperación válido en la URL.</p>
          <p>Por favor, utiliza el enlace completo que recibiste en tu correo electrónico.</p>
          <div class="action-buttons">
            <button @click="goToForgotPassword" class="btn-primary">
              Solicitar Recuperación
            </button>
            <button @click="goToHome" class="btn-secondary">
              Ir al Inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getApiUrl, config } from '@/config';

export default {
  name: 'AppPasswordReset',
  data() {
    return {
      isLoading: true,
      validToken: false,
      resetComplete: false,
      isResetting: false,
      token: '',
      newPassword: '',
      confirmPassword: '',
      passwordError: '',
      confirmError: '',
      errorMessage: ''
    }
  },
  async mounted() {
    await this.verifyToken();
  },
  methods: {
    async verifyToken() {
      try {
        // Obtener el token de la URL
        this.token = this.$route.query.token;
        
        if (!this.token) {
          this.validToken = false;
          this.errorMessage = 'Token no encontrado en la URL';
          this.isLoading = false;
          return;
        }

        // Verificar si el token es válido
        const response = await fetch(getApiUrl(config.endpoints.verifyResetToken), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: this.token })
        });

        if (response.ok) {
          const data = await response.json();
          this.validToken = data.valid || false;
          if (!this.validToken) {
            this.errorMessage = 'El token ha expirado o ya fue utilizado';
          }
        } else {
          this.validToken = false;
          const errorData = await response.json();
          this.errorMessage = errorData.error || 'Error al verificar el token';
        }
      } catch (error) {
        this.validToken = false;
        this.errorMessage = 'Error de conexión. Por favor, intenta más tarde.';
        console.error('Error verificando token:', error);
      } finally {
        this.isLoading = false;
      }
    },

    validatePassword() {
      this.passwordError = '';
      this.confirmError = '';

      // Validar nueva contraseña
      if (this.newPassword.length < 8) {
        this.passwordError = 'La contraseña debe tener al menos 8 caracteres';
        return false;
      }

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;
      if (!passwordRegex.test(this.newPassword)) {
        this.passwordError = 'La contraseña debe contener al menos: 1 minúscula, 1 mayúscula, 1 número y 1 carácter especial';
        return false;
      }

      // Validar confirmación
      if (this.newPassword !== this.confirmPassword) {
        this.confirmError = 'Las contraseñas no coinciden';
        return false;
      }

      return true;
    },

    async resetPassword() {
      if (!this.validatePassword()) {
        return;
      }

      this.isResetting = true;
      try {
        const response = await fetch(getApiUrl(config.endpoints.resetPassword), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            token: this.token,
            newPassword: this.newPassword
          })
        });

        if (response.ok) {
          this.resetComplete = true;
          console.log('Contraseña restablecida exitosamente');
        } else {
          const errorData = await response.json();
          this.errorMessage = errorData.error || 'Error al restablecer la contraseña';
          this.validToken = false;
        }
      } catch (error) {
        this.errorMessage = 'Error de conexión. Por favor, intenta más tarde.';
        this.validToken = false;
        console.error('Error restableciendo contraseña:', error);
      } finally {
        this.isResetting = false;
      }
    },

    goToLogin() {
      this.$router.push('/login');
    },

    goToHome() {
      this.$router.push('/');
    },

    goToForgotPassword() {
      // Por ahora redirigir al login, más tarde se puede crear una página específica
      this.$router.push('/login');
    }
  }
}
</script>

<style scoped>
.password-reset {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.reset-container {
  width: 100%;
  max-width: 500px;
}

.reset-card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
}

/* Estados de carga */
.loading-state {
  padding: 20px 0;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Formulario de reset */
.reset-form-state {
  text-align: left;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h2 {
  color: #333;
  margin-bottom: 10px;
  font-size: 1.8rem;
}

.header p {
  color: #666;
  margin: 0;
}

.reset-form {
  max-width: 400px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #667eea;
}

.form-group input.error {
  border-color: #dc3545;
}

.password-requirements {
  margin-top: 8px;
}

.password-requirements small {
  color: #666;
  font-size: 0.875rem;
  line-height: 1.4;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 5px;
}

.form-actions {
  margin-top: 30px;
  text-align: center;
}

/* Estados de éxito y error */
.success-state,
.error-state {
  padding: 20px 0;
}

.success-icon,
.error-icon {
  font-size: 4rem;
  margin-bottom: 20px;
}

.success-state h2 {
  color: #28a745;
  margin-bottom: 16px;
  font-size: 1.8rem;
}

.error-state h2 {
  color: #dc3545;
  margin-bottom: 16px;
  font-size: 1.8rem;
}

.success-state p,
.error-state p {
  color: #666;
  margin-bottom: 12px;
  line-height: 1.5;
}

.error-message {
  color: #dc3545;
  font-weight: 500;
  margin-bottom: 20px;
}

.error-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  text-align: left;
}

.error-details p {
  margin-bottom: 10px;
  font-weight: 500;
  color: #495057;
}

.error-details ul {
  margin: 0;
  padding-left: 20px;
}

.error-details li {
  margin-bottom: 8px;
  color: #6c757d;
}

/* Botones */
.action-buttons {
  margin-top: 30px;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
  transform: translateY(-2px);
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

/* Responsive */
@media (max-width: 768px) {
  .reset-card {
    padding: 30px 20px;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: 250px;
  }
}
</style>
